import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './PnLSummary.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function PnLSummary({ tradesData }) {
  if (!tradesData || !tradesData.completedTrades) {
    return (
      <div className="pnl-summary">
        <div className="card">
          <p>No trade data available. Please upload a trade file first.</p>
        </div>
      </div>
    );
  }

  const completedTrades = tradesData.completedTrades || [];
  const openTrades = tradesData.openTrades || [];
  const stats = tradesData.stats || {};

  // Calculate cumulative P&L by date
  const cumulativePnLData = useMemo(() => {
    if (!completedTrades || completedTrades.length === 0) {
      return { labels: [], values: [] };
    }

    // Group trades by date and calculate daily P&L
    const dailyPnL = {};
    completedTrades.forEach(trade => {
      const date = trade.tradeDate || trade.date || new Date().toISOString().split('T')[0];
      if (!dailyPnL[date]) {
        dailyPnL[date] = 0;
      }
      dailyPnL[date] += trade.pnl || 0;
    });

    // Sort dates chronologically
    const sortedDates = Object.keys(dailyPnL).sort((a, b) => {
      return new Date(a) - new Date(b);
    });

    // Calculate cumulative P&L
    let cumulative = 0;
    const labels = [];
    const values = [];

    sortedDates.forEach(date => {
      cumulative += dailyPnL[date];
      labels.push(new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      values.push(cumulative);
    });

    return { labels, values };
  }, [completedTrades]);

  // Prepare chart data
  const chartData = {
    labels: cumulativePnLData.labels,
    datasets: [
      {
        label: 'Cumulative P&L',
        data: cumulativePnLData.values,
        borderColor: '#667eea',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#667eea',
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            return `Cumulative P&L: ₹${value.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date'
        },
        grid: {
          display: false
        }
      },
      y: {
        title: {
          display: true,
          text: 'Cumulative P&L (₹)'
        },
        ticks: {
          callback: function(value) {
            return '₹' + value.toFixed(0);
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    }
  };

  const netPnl = stats.netPnl || 0;
  const totalCompletedBuyValue = completedTrades.reduce((sum, t) => sum + (Number(t.buyValue) || 0), 0);
  const netPnlRealizedPercent = totalCompletedBuyValue > 0 ? (netPnl / totalCompletedBuyValue) * 100 : 0;
  const totalProfits = stats.totalProfits || 0;
  const totalLosses = stats.totalLosses || 0;
  const avgProfitPerWin = stats.avgProfitPerWin || 0;
  const avgLossPerTrade = stats.avgLossPerTrade || 0;
  const profitFactor = stats.profitFactor || 0;
  const openPositionValue = openTrades.reduce((sum, t) => sum + t.investedValue, 0);

  return (
    <div className="pnl-summary">
      <h2>P&L Summary</h2>

      {/* Net P&L Hero Card */}
      <div className={`pnl-hero-card ${netPnl >= 0 ? 'profit' : 'loss'}`}>
        <div className="pnl-hero-label">Net P&L (Realized %)</div>
        <div className="pnl-hero-value">
          ₹{netPnl.toFixed(2)} ({netPnlRealizedPercent.toFixed(2)}%)
        </div>
      </div>

      {/* P&L Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Profits</div>
          <div className="stat-value profit">₹{totalProfits.toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Losses</div>
          <div className="stat-value loss">₹{totalLosses.toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Profit/Win</div>
          <div className="stat-value">{avgProfitPerWin.toFixed(2) > 0 ? `₹${avgProfitPerWin.toFixed(2)}` : '₹0.00'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Loss/Trade</div>
          <div className="stat-value">{avgLossPerTrade.toFixed(2) > 0 ? `₹${avgLossPerTrade.toFixed(2)}` : '₹0.00'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Profit Factor</div>
          <div className="stat-value">{profitFactor === 'Infinity' ? '∞' : profitFactor.toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Open Position Value</div>
          <div className="stat-value">₹{openPositionValue.toFixed(2)}</div>
        </div>
      </div>

      {/* Cumulative P&L Chart */}
      {cumulativePnLData.labels.length > 0 && (
        <div className="card">
          <h3>Cumulative P&L Trend</h3>
          <div className="chart-wrapper">
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>
      )}

      {/* Detailed P&L Table */}
      <div className="card">
        <h3>Detailed P&L</h3>
        {completedTrades.length > 0 ? (
          <div className="table-container">
            <table className="trades-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Buy Value</th>
                  <th>Sell Value</th>
                  <th>P/L Amount</th>
                  <th>P/L %</th>
                </tr>
              </thead>
              <tbody>
                {completedTrades.map((trade, index) => (
                  <tr key={index}>
                    <td>{trade.symbol}</td>
                    <td>₹{trade.buyValue.toFixed(2)}</td>
                    <td>₹{trade.sellValue.toFixed(2)}</td>
                    <td className={trade.pnl >= 0 ? 'profit' : 'loss'}>
                      ₹{trade.pnl.toFixed(2)}
                    </td>
                    <td className={trade.pnlPercent >= 0 ? 'profit' : 'loss'}>
                      {trade.pnlPercent.toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No completed trades yet.</p>
        )}
      </div>
    </div>
  );
}

export default PnLSummary;
